import * as React from 'react';
import moment from 'moment';
import { Box, Popover, Typography } from '@material-ui/core';
import {
  CustomAutocomplete,
  CustomButton,
  CustomDatePicker,
  CustomInputText,
  NumberFormatCustom
} from '../../custom-forms';
import {
  ColumnFilter,
  ColumnType,
  DateRangeFilter,
  GridColumnFor,
  isDateRangeFilter,
  isRangeFilter,
  RangeFilter
} from '../../types/grid-filter.types';
import { useTranslate } from '../../hooks/useTranslate';

export interface FilterPopoverProps<TKey extends string> {
  anchorEl: HTMLElement | null;
  column: GridColumnFor<TKey> | null;
  value?: ColumnFilter;
  options: ColumnFilter[];
  getOptionLabel: (option: ColumnFilter | null) => string;
  onApply: (value: ColumnFilter | null) => void;
  onClose: () => void;
}

const asRange = (v?: ColumnFilter): RangeFilter =>
  v && isRangeFilter(v) ? v : { min: null, max: null };
const asDateRange = (v?: ColumnFilter): DateRangeFilter =>
  v && isDateRangeFilter(v) ? v : { from: null, to: null };
const asText = (v?: ColumnFilter): string => (typeof v === 'string' ? v : '');

/** Column-filter popover (range / date / select / text) — reusable across any grid. */
function FilterPopover<TKey extends string>({
  anchorEl,
  column,
  value,
  options,
  getOptionLabel,
  onApply,
  onClose
}: FilterPopoverProps<TKey>) {
  const t = useTranslate();
  const [min, setMin] = React.useState('');
  const [max, setMax] = React.useState('');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [text, setText] = React.useState('');
  const [selected, setSelected] = React.useState<ColumnFilter | null>(null);
  const [dateError, setDateError] = React.useState(false);

  React.useEffect(() => {
    if (!column) return;
    const range = asRange(value);
    setMin(range.min != null ? String(range.min) : '');
    setMax(range.max != null ? String(range.max) : '');
    const dateRange = asDateRange(value);
    setFrom(dateRange.from ?? '');
    setTo(dateRange.to ?? '');
    setText(asText(value));
    setSelected(value != null && !isRangeFilter(value) && !isDateRangeFilter(value) ? value : null);
    setDateError(false);
  }, [column, value]);

  const apply = () => {
    if (!column) return;
    if (column.type === ColumnType.Range) {
      if (min === '' && max === '') onApply(null);
      else onApply({ min: min === '' ? null : Number(min), max: max === '' ? null : Number(max) });
    } else if (column.type === ColumnType.Date) {
      if (from !== '' && to !== '' && !moment(from).isBefore(moment(to), 'day')) {
        setDateError(true);
        return;
      }
      if (from === '' && to === '') onApply(null);
      else onApply({ from: from === '' ? null : from, to: to === '' ? null : to });
    } else if (column.type === ColumnType.Select) {
      onApply(selected || null);
    } else {
      onApply(text.trim() || null);
    }
  };

  return (
    <Popover
      open={Boolean(anchorEl && column)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box style={{ padding: 12, width: 240 }}>
        <Typography style={{ fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 18 }}>
          {t('GRID_FILTER_BY', 'Filtrează')} · {column?.label}
        </Typography>

        {column?.type === ColumnType.Range && (
          <Box style={{ display: 'flex', gap: 8 }}>
            <CustomInputText
              size="small"
              InputProps={{ inputComponent: NumberFormatCustom }}
              placeholder={t('GRID_FILTER_MIN', 'min')}
              value={min}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMin(e.target.value)}
              fullWidth
            />
            <CustomInputText
              size="small"
              InputProps={{ inputComponent: NumberFormatCustom }}
              placeholder={t('GRID_FILTER_MAX', 'max')}
              value={max}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMax(e.target.value)}
              fullWidth
            />
          </Box>
        )}

        {column?.type === ColumnType.Date && (
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <CustomDatePicker
              label={t('GRID_FILTER_DATE_FROM', 'De la')}
              value={from || null}
              onChange={(v) => {
                setFrom(v ?? '');
                setDateError(false);
              }}
              disablePastDates
              error={dateError}
            />
            <CustomDatePicker
              label={t('GRID_FILTER_DATE_TO', 'Până la')}
              value={to || null}
              onChange={(v) => {
                setTo(v ?? '');
                setDateError(false);
              }}
              disablePastDates
              error={dateError}
            />
            {dateError && (
              <Typography style={{ color: '#d32f2f', fontSize: 11 }}>
                {t('GRID_FILTER_DATE_RANGE_ERROR', 'De la trebuie sa fie mai mica decat pana la')}
              </Typography>
            )}
          </Box>
        )}

        {column?.type === ColumnType.Select && (
          <CustomAutocomplete
            size="small"
            options={options}
            value={selected}
            getOptionLabel={(o: ColumnFilter | null) => getOptionLabel(o)}
            onChange={(_e: React.SyntheticEvent, v: ColumnFilter | null) => setSelected(v)}
            placeholder={t('GRID_FILTER_ALL', 'Toate')}
          />
        )}

        {column?.type === ColumnType.Text && (
          <CustomInputText
            placeholder={t('GRID_FILTER_CONTAINS', 'conține…')}
            value={text}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setText(e.target.value)}
            fullWidth
          />
        )}

        <Box style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <CustomButton variant="outlined" style={{ flex: 1 }} onClick={() => onApply(null)}>
            {t('GRID_FILTER_CLEAR', 'Șterge')}
          </CustomButton>
          <CustomButton style={{ flex: 1 }} onClick={apply}>
            {t('GRID_FILTER_APPLY', 'Aplică')}
          </CustomButton>
        </Box>
      </Box>
    </Popover>
  );
}

export default FilterPopover;
